/**
 * Theme state — theme step 2 (2026-10-05): dark (default) + a light palette that ONLY the admin role may use for now;
 * theme step 3 (2026-10-06): + a graphite palette (ground #101216, cards #1A1D23) — open to every signed-in user
 * (owner 2026-10-06); light remains admin-only.
 *
 * Source of truth for the ACTIVE theme is the `data-theme` attribute on <html>:
 *   absent / "dark" → dark tokens (:root);  "light" → :root[data-theme="light"];  "graphite" → :root[data-theme="graphite"].
 * The admin's CHOICE is stored in localStorage `saa_theme` ("light" | "graphite" | "dark"). It is honoured only for the
 * admin role:
 *   - before first paint, public/index.html applies "light"/"graphite" only when the cached server role (saa_auth_cache, written
 *     by AuthContext from /api/auth/me) is admin;
 *   - after that, <ThemeGate> re-decides from the server-authoritative role in AuthContext on every change — a
 *     non-admin is always dark, whatever the key says.
 * JS colour sinks (charts, SVG attributes, keyframes) subscribe through useTheme()/usePalette() and re-render or
 * applyOptions when the attribute changes — no reload.
 */
import { useSyncExternalStore } from 'react';
import { paletteFor, paletteRgba, PaletteToken, ThemeName } from './palette';
import { subscribePrint } from '../print/printMode';

/** The non-default themes (admin-only). Dark is the absence of the attribute. */
const ALT_THEMES: readonly ThemeName[] = ['light', 'graphite'];
const asAltTheme = (v: string | null): ThemeName => (v && (ALT_THEMES as readonly string[]).includes(v) ? (v as ThemeName) : 'dark');

export const THEME_STORAGE_KEY = 'saa_theme';

/** The admin's stored preference (not the active theme). */
// Owner 2026-10-08: the terminal default is GRAPHITE. No stored choice → graphite; an explicit "dark" choice is kept.
export const DEFAULT_TERMINAL_THEME: ThemeName = 'graphite';
export function getStoredTheme(): ThemeName {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (v === null) return DEFAULT_TERMINAL_THEME;
    return asAltTheme(v);
  } catch {
    return DEFAULT_TERMINAL_THEME;
  }
}

export function setStoredTheme(theme: ThemeName): void {
  try { window.localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* storage may be unavailable */ }
}

/** The theme currently applied to the document. */
export function getActiveTheme(): ThemeName {
  if (typeof document === 'undefined') return 'dark';
  return asAltTheme(document.documentElement.getAttribute('data-theme'));
}

/** Apply a theme to <html>. Dark removes the attribute (the DOM is then exactly the pre-theme DOM). */
export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  if (theme === 'light' || theme === 'graphite') {
    if (el.getAttribute('data-theme') !== theme) el.setAttribute('data-theme', theme);
  } else if (el.hasAttribute('data-theme')) {
    el.removeAttribute('data-theme');
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', paletteFor(theme)['bg']);
}

/** The theme a caller with this role gets: light / graphite only for admin with that stored choice. */
// Owner 2026-10-05: the gateway pages (landing, sign-in/registration flow, plans, Plan & Access, terms) stay in the
// original dark look whatever the theme — the light and graphite themes apply to the analytical terminal only.
const GATEWAY_PATHS = ['/', '/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/invite',
  '/pricing', '/pricing.html', '/terms', '/account', '/account/security'];
export function isGatewayPath(path: string = typeof window !== 'undefined' ? window.location.pathname : '/'): boolean {
  const p = path.replace(/\/+$/, '') || '/';
  return GATEWAY_PATHS.includes(p);
}

// Owner 2026-10-06: graphite is open to every signed-in user; light stays an admin-only preview.
export function effectiveTheme(role: string | undefined, stored: ThemeName = getStoredTheme(), path?: string): ThemeName {
  if (isGatewayPath(path)) return 'dark';
  if (stored === 'graphite' && role) return 'graphite';
  if (stored === 'light' && role === 'admin') return 'light';
  return 'dark';
}

function subscribe(onChange: () => void): () => void {
  // Print mode (src/print/printMode.ts) switches to the light tokens and notifies synchronously, so JS colour sinks
  // repaint before the print snapshot rather than on the next MutationObserver tick.
  const offPrint = subscribePrint(onChange);
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return offPrint;
  const mo = new MutationObserver(onChange);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => { mo.disconnect(); offPrint(); };
}

/** The active theme; re-renders the caller when it changes. */
export function useTheme(): ThemeName {
  return useSyncExternalStore(subscribe, getActiveTheme, () => 'dark' as ThemeName);
}

/** Theme-aware palette for JS colour sinks: `pal[name]` (hex) and `rgba(name, alpha)`. */
export function usePalette(): { theme: ThemeName; pal: Record<PaletteToken, string>; rgba: (name: PaletteToken, alpha: string | number) => string } {
  const theme = useTheme();
  return { theme, pal: paletteFor(theme), rgba: (name, alpha) => paletteRgba(name, alpha, theme) };
}
